"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import SearchFilterBar, { ViewType, FilterType } from "./search-filter-bar";
import BlogCard from "./blog-card";
import BlogListItem from "./blog-list-item";
import cn from "@/utils/cn";
import { BlogPostDocument } from "../../../../../prismicio-types";

interface BlogClientWrapperProps {
  blogContents: BlogPostDocument[];
  initialSearchQuery: string;
  initialFilterType: FilterType;
  initialViewType: ViewType;
}

export default function BlogClientWrapper({
  blogContents,
  initialSearchQuery,
  initialFilterType,
  initialViewType,
}: BlogClientWrapperProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const [searchQuery] = useState(initialSearchQuery);
  const [filterType, setFilterType] = useState(initialFilterType);
  const [viewType, setViewType] = useState(initialViewType);

  // Update URL when search/filter changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (searchQuery) {
      params.set("search", searchQuery);
    } else {
      params.delete("search");
    }

    if (filterType !== "all") {
      params.set("filter", filterType);
    } else {
      params.delete("filter");
    }

    if (viewType !== "card") {
      params.set("view", viewType);
    } else {
      params.delete("view");
    }

    // Reset to page 1 when search/filter changes
    params.delete("page");

    const newUrl = params.toString() ? `?${params.toString()}` : "";
    router.push(`/resources${newUrl}`, { scroll: false });
  }, [searchQuery, filterType, viewType, router, searchParams]);

  return (
    <div className="w-full min-w-[317px] xl:min-w-[1120px]">
      {/* Search and Filter Bar */}
      <div className="mb-8 w-full bg-white px-[39px] md:px-[36px] lg:px-20 ">
        <SearchFilterBar
          selectedFilter={filterType}
          onFilterChange={setFilterType}
          viewType={viewType}
          onViewTypeChange={setViewType}
        />
      </div>
      <div className="mx-auto mb-[70px] max-w-[1120px] px-[39px] pt-[104px] md:px-[36px] lg:px-20 xl:px-0">
        {/* Blog Content */}
        {blogContents.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center py-12">
            <p className="mb-2 text-lg text-gray-600">No articles found</p>
            <p className="text-sm text-gray-500">
              Try adjusting your search terms or filters
            </p>
          </div>
        ) : (
          <div
            className={cn(
              "w-full",
              viewType === "card"
                ? "grid grid-cols-1 gap-[45px] justify-items-center md:grid-cols-3 lg:grid-cols-3"
                : "flex flex-col gap-1",
            )}
          >
            {blogContents.map((blogContent) =>
              viewType === "card" ? (
                <BlogCard
                  key={blogContent.uid}
                  publishDate={blogContent.last_publication_date}
                  blogLink={blogContent.url}
                  blogTitle={blogContent.data.blog_title}
                  blogExcerpt={blogContent.data.blog_excerpt}
                  coverImage={blogContent.data.hero_image}
                  blogDate={blogContent.data.blog_date}
                />
              ) : (
                <BlogListItem
                  key={blogContent.uid}
                  publishDate={blogContent.last_publication_date}
                  blogLink={blogContent.url}
                  blogTitle={blogContent.data.blog_title}
                  blogExcerpt={blogContent.data.blog_excerpt}
                  coverImage={blogContent.data.hero_image}
                  blogDate={blogContent.data.blog_date}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}
